import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { periodLabel } from "@/lib/pricing";
import { fmtPlainCVE } from "@/components/PaymentLogos";
import { V4StatusBadge } from "@/components/Vinti4Payments";
import { Button } from "@/components/ui/button";
import { ArrowsClockwise, CircleNotch, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function ConfigStatus({ c }) {
  const ok = c.available;
  return (
    <div className={`flex items-start gap-3 rounded-lg border p-4 text-sm ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="vinti4-config-status">
      {ok ? <CheckCircle size={20} weight="fill" className="shrink-0" /> : <WarningCircle size={20} weight="fill" className="shrink-0" />}
      <div>
        <div className="font-semibold">{ok ? `Vinti4 ativo · ambiente ${c.env}` : "Vinti4 desativado"}</div>
        {!ok && <div data-testid="vinti4-config-error">{c.error}</div>}
        <div className="mt-1 text-xs opacity-80">Guardar cartão (tokenização): {c.tokenization ? "ativo" : "desativado até aprovação SISP"} · Prestações: {c.installments ? "ativo" : "desativado até aprovação SISP"}</div>
      </div>
    </div>
  );
}

export function Vinti4AdminSection() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => { api.get("/platform/vinti4").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const run = async (key, url) => {
    setBusy(key);
    try {
      const { data } = await api.post(url);
      toast.success(data.checked !== undefined ? `${data.checked} pagamento(s) verificados` : `Estado SISP: ${data.sisp?.transactionStatusDescription || data.payment.status}`);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };
  return (
    <section className="space-y-4 rounded-xl border border-border bg-card p-5" data-testid="vinti4-admin-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold">Pagamentos Vinti4 (SISP)</h2>
        <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy || !d.config.available} onClick={() => run("all", "/platform/vinti4/reconcile")} data-testid="vinti4-reconcile-btn">
          {busy === "all" ? <CircleNotch className="animate-spin" /> : <><ArrowsClockwise size={15} className="mr-1" /> Reconciliar pendentes</>}
        </Button>
      </div>
      <ConfigStatus c={d.config} />
      {d.items.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="vinti4-admin-empty">Ainda não há pagamentos Vinti4.</p> : d.items.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 py-2 text-sm last:border-0" data-testid={`vinti4-admin-row-${p.id}`}>
          <div>
            <div className="font-medium">{p.org_name} · {p.plan_name} {periodLabel(p.months)} · {fmtPlainCVE(p.amount_cve)}</div>
            <div className="text-xs text-muted-foreground"><span className="font-mono">{p.merchant_ref}</span> · {fmtDate(p.created_at)}{p.note ? ` · ${p.note}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <V4StatusBadge status={p.status} />
            {["pending", "pending_verification"].includes(p.status) && (
              <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => run(p.id, `/platform/vinti4/payments/${p.id}/check-status`)} data-testid={`vinti4-admin-check-${p.id}`}>
                {busy === p.id ? <CircleNotch className="animate-spin" /> : "Verificar"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
